import { useCallback, useState } from 'react';

import { ArticleModuleMediaCollapsibleMetadataBox } from '#components/Article/Modules/ArticleModuleMediaCollapsibleMetadataBox';
import { MediaLivestream } from '#components/Media/MediaLivestream';
import { MediaContextPlaybackStatus } from '#cutils/context/MediaContext';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import { logger } from '#utils/logging/logger';

import styles from '../../../../shared/styles/mediacontainer.module.scss';

import { ArticleModuleMedia } from './ArticleModuleMedia';
import { ARTICLE_MODULE_MEDIA_IMAGE_SIZES } from './ArticleModuleMediaSize';
import type { ArticleModuleLivestreamFragment } from './__generated__/ArticleModuleLivestream.generated';

type Props = {
	livestreamModule: ArticleModuleLivestreamFragment;
	publicationDate: string | null | undefined;
};

export function ArticleModuleLivestream({ publicationDate, livestreamModule }: Props) {
	const thumbnail = livestreamModule.thumbnail;

	const [mediaPlaybackStatus, setMediaPlaybackStatus] = useState(MediaContextPlaybackStatus.STOPPED);

	const handlePlaybackStatusChange = useCallback((e: MediaContextPlaybackStatus) => {
		setMediaPlaybackStatus(e);
	}, []);

	if (!thumbnail) {
		logger.error(`Thumbnail for livestream with id '%s' not found.`, livestreamModule.rowId);

		return null;
	}

	const livestreamStartDate = livestreamModule?.livestream?.start ?? null;
	const livestreamEndDate = livestreamModule.livestream?.end ?? null;
	const livestreamHlsUrl = livestreamModule.livestream?.hls ?? null;
	const onDemandVideos = (livestreamModule.livestream?.programme?.videoFiles?.nodes ?? [])
		.filter(notNullOrUndefined)
		.map(({ publicLocation }) => publicLocation)
		.filter(notNullOrUndefined);

	if (!livestreamHlsUrl) {
		logger.error(`Module is of type 'livestream', but no url was found in for module with id '%s'.`, livestreamModule.rowId);

		return null;
	}

	const collapsibleMetadataBox = (
		<ArticleModuleMediaCollapsibleMetadataBox
			articleModule={livestreamModule}
			mediaPlaybackStatus={mediaPlaybackStatus}
			moduleType={'LIVESTREAM'}
		/>
	);
	const livestream = (
		<div className={styles.mediaRoot}>
			<MediaLivestream
				copyright={livestreamModule.copyright}
				livestreamStartDate={livestreamStartDate}
				livestreamEndDate={livestreamEndDate}
				liveStreamHlsUrl={livestreamHlsUrl}
				thumbnailAltText={thumbnail.altText}
				thumbnailCopyright={thumbnail.copyright}
				thumbnailUrl={thumbnail.url}
				title={livestreamModule.title || undefined}
				moduleId={livestreamModule.rowId}
				videos={onDemandVideos}
				onPlayStatusChange={handlePlaybackStatusChange}
				publicationDate={publicationDate}
				sizes={ARTICLE_MODULE_MEDIA_IMAGE_SIZES}
			/>
		</div>
	);

	return <ArticleModuleMedia collapsibleMetadataBox={collapsibleMetadataBox} media={livestream} />;
}
